import { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, onSnapshot, collection, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { logMonitoringEvent } from '../utils/monitoringLogger';

export const usePresence = (roomId, userId) => {
  const [isLive, setIsLive] = useState(false);

  useEffect(() => {
    if (!roomId) return;
    const unsub = onSnapshot(collection(db, 'rooms', roomId, 'presence'), (snap) => {
      const now = Date.now();
      const active = snap.docs.some(d => {
        const data = d.data();
        const lastActive = data.lastActive?.toDate?.() || new Date(data.lastActive);
        return now - lastActive.getTime() < 240000;
      });
      setIsLive(active);
    });
    return unsub;
  }, [roomId]);

  useEffect(() => {
    if (!roomId || !userId) return;
    const ping = async () => {
      try {
        await setDoc(doc(db, 'rooms', roomId, 'presence', userId), {
          lastActive: Timestamp.now(),
          isVoting: true,
        }, { merge: true });
      } catch (e) {
        logMonitoringEvent('error', 'firestore', `Presence ping failed: ${e.message}`, { userId, metadata: { roomId } });
      }
    };
    ping();
    const interval = setInterval(ping, 180000);
    return () => clearInterval(interval);
  }, [roomId, userId]);

  const markActive = useCallback(async () => {
    if (!roomId || !userId) return;
    try {
      await setDoc(doc(db, 'rooms', roomId, 'presence', userId), {
        lastActive: Timestamp.now(),
        isVoting: true,
      }, { merge: true });
    } catch (e) {
      console.error('Failed to update presence:', e);
      logMonitoringEvent('error', 'firestore', `Failed to update presence: ${e.message}`, {
        userId, metadata: { roomId },
      });
    }
  }, [roomId, userId]);

  return { isLive, markActive };
};
